/**
 * Production Logging Service
 * 
 * Replaces console.log statements with proper structured logging
 * for production environments. Supports different log levels and
 * integrates with Supabase database for persistent logging.
 */

import { supabase } from '../lib/supabase';

export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: Record<string, any>;
  userId?: string;
  sessionId?: string;
}

class LoggingService {
  private isDevelopment: boolean;
  private logLevel: LogLevel;

  constructor() {
    this.isDevelopment = import.meta.env.DEV;
    // In production, only log warnings and errors to avoid console spam
    this.logLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.ERROR;
  }

  private shouldLog(level: LogLevel): boolean {
    const levels = [LogLevel.DEBUG, LogLevel.INFO, LogLevel.WARN, LogLevel.ERROR];
    const currentLevelIndex = levels.indexOf(this.logLevel);
    const messageLevelIndex = levels.indexOf(level);
    return messageLevelIndex >= currentLevelIndex;
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    context?: Record<string, any> | unknown
  ): LogEntry {
    // Safely convert unknown context to Record<string, any>
    let safeContext: Record<string, any> | undefined;
    if (context !== null && context !== undefined) {
      if (typeof context === 'object') {
        safeContext = context as Record<string, any>;
      } else {
        safeContext = { value: context };
      }
    }

    return {
      level,
      message,
      timestamp: new Date().toISOString(),
      context: safeContext,
      userId: this.getCurrentUserId(),
      sessionId: this.getCurrentSessionId(),
    };
  }

  private getCurrentUserId(): string | undefined {
    // Try to get user ID from auth context or localStorage
    try {
      const authData = localStorage.getItem('supabase.auth.token');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed?.user?.id;
      }
    } catch {
      // Ignore errors
    }
    return undefined;
  }

  private getCurrentSessionId(): string | undefined {
    // Generate or retrieve session ID
    let sessionId = sessionStorage.getItem('cyclone_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('cyclone_session_id', sessionId);
    }
    return sessionId;
  }

  private outputLog(entry: LogEntry): void {
    if (!this.shouldLog(entry.level)) {
      return;
    }

    // In development, use console with colors
    if (this.isDevelopment) {
      const colors = {
        [LogLevel.DEBUG]: '#888',
        [LogLevel.INFO]: '#2196F3',
        [LogLevel.WARN]: '#FF9800',
        [LogLevel.ERROR]: '#F44336',
      };

      const color = colors[entry.level];
      console.log(
        `%c[${entry.level.toUpperCase()}] ${entry.timestamp}`,
        `color: ${color}; font-weight: bold;`,
        entry.message,
        entry.context ? entry.context : ''
      );
    } else {
      // In production, send to external logging service
      // Fire and forget - don't block on async operation
      this.sendToExternalService(entry).catch(err => {
        console.warn('Failed to send log to external service', err);
      });
    }
  }

  private async sendToExternalService(entry: LogEntry): Promise<void> {
    try {
      // Write to database for persistent logging
      await supabase.from('application_logs').insert({
        level: entry.level,
        message: entry.message,
        context: entry.context || {},
        user_id: entry.userId,
        service: 'frontend',
        stack_trace: entry.context?.stack,
        environment: import.meta.env.MODE || 'production',
      });

      // Integrate with Sentry for error tracking (optional)
      try {
        const { SentryService } = await import('./monitoring/sentryService');
        
        if (!SentryService.isReady()) {
          // Sentry not initialized, skip
          return;
        }
        
        // Only send errors and warnings to Sentry
        if (entry.level === LogLevel.ERROR) {
          const error = entry.context?.error instanceof Error 
            ? entry.context.error 
            : new Error(entry.message);
          
          SentryService.captureException(error, {
            logger: 'LoggingService',
            message: entry.message,
            ...entry.context,
          });
        } else if (entry.level === LogLevel.WARN) {
          SentryService.captureMessage(entry.message, 'warning', {
            logger: 'LoggingService',
            ...entry.context,
          });
        }
      } catch (sentryError) {
        // Sentry is optional, don't fail if it's not available
        console.debug('Sentry not available:', sentryError);
      }
    } catch (error) {
      // Fallback to console if database logging fails
      console.log(JSON.stringify(entry));
      console.warn('Failed to write log to database', error);
    }
  }

  // Public logging methods - accept unknown for flexibility
  debug(message: string, context?: Record<string, any> | unknown): void {
    const entry = this.createLogEntry(LogLevel.DEBUG, message, context);
    this.outputLog(entry);
  }

  info(message: string, context?: Record<string, any> | unknown): void {
    const entry = this.createLogEntry(LogLevel.INFO, message, context);
    this.outputLog(entry);
  }

  warn(message: string, context?: Record<string, any> | unknown): void {
    const entry = this.createLogEntry(LogLevel.WARN, message, context);
    this.outputLog(entry);
  }

  error(message: string, context?: Record<string, any> | unknown): void {
    const entry = this.createLogEntry(LogLevel.ERROR, message, context);
    this.outputLog(entry);
  }

  // Convenience methods for common patterns
  logUserAction(action: string, context?: Record<string, any>): void {
    this.info(`User action: ${action}`, { action, ...context });
  }

  logApiCall(method: string, url: string, status?: number, context?: Record<string, any>): void {
    const level = status && status >= 400 ? LogLevel.ERROR : LogLevel.INFO;
    const entry = this.createLogEntry(level, `API ${method} ${url}`, {
      method,
      url,
      status,
      ...context,
    });
    this.outputLog(entry);
  }

  logError(error: Error, context?: Record<string, any>): void {
    this.error(`Error: ${error.message}`, {
      error: error.message,
      stack: error.stack,
      ...context,
    });
  }

  logPerformance(operation: string, duration: number, context?: Record<string, any>): void {
    this.info(`Performance: ${operation} took ${duration}ms`, {
      operation,
      duration,
      ...context,
    });
  }
}

// Export singleton instance
export const logger = new LoggingService();

// Export for testing
export { LoggingService };
